import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/shallow";
import { useOpenState } from "@/shared/hooks/useOpenState";
import { ChatCircleTextIcon } from "@phosphor-icons/react";

import { FeedbackList } from "@/features/feedback/components/FeedbackList";
import { FeedbackListItem } from "@/features/feedback/components/FeedbackListItem";
import { FeedbackCategory } from "@/features/feedback/components/FeedbackCategory";
import { FeedbackModal } from "@/features/feedback/components/FeedbackModal";
import { api } from "@/services/api";
import { useBoundStore } from "@/app/store";
import { usePageLayout } from "@/app/hooks/usePageLayout";

export function FeedbackRoute() {
  const { isOpen, handleOpenChange } = useOpenState({ id: "feedback" });
  const { data: feedbacks = [], isLoading } = api.hooks.feedback.useFeedbacks();
  const proposedFeedbacks = feedbacks.filter((feedback) => feedback.status === "proposed");
  const inProgressFeedbacks = feedbacks.filter((feedback) => feedback.status === "in-progress");
  const completedFeedbacks = feedbacks.filter((feedback) => feedback.status === "completed");
  const authUserId = useBoundStore(useShallow((state) => state.user.id));

  const handleOpenFeedbackModal = useCallback(() => {
    handleOpenChange(true);
  }, [handleOpenChange]);

  const handleCloseFeedbackModal = () => {
    handleOpenChange(false);
  };

  const pageLayoutConfig = useMemo(
    () => ({
      fab: { icon: <ChatCircleTextIcon />, onPress: handleOpenFeedbackModal },
      navbar: { title: "Feedback" },
    }),
    [handleOpenFeedbackModal]
  );

  usePageLayout(pageLayoutConfig);

  if (isLoading) {
    return <>Loading...</>;
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        <FeedbackCategory color="default" name="Purposed" numberOfVotes={proposedFeedbacks.length}>
          <FeedbackList className="mt-6">
            {proposedFeedbacks.map((feedback) => (
              <FeedbackListItem
                key={feedback.id}
                voted={authUserId ? feedback.votes.includes(authUserId) : false}
                {...feedback}
              />
            ))}
          </FeedbackList>
        </FeedbackCategory>

        <FeedbackCategory color="warning" name="In Progress" numberOfVotes={inProgressFeedbacks.length}>
          <FeedbackList className="mt-6">
            {inProgressFeedbacks.map((feedback) => (
              <FeedbackListItem
                key={feedback.id}
                voted={authUserId ? feedback.votes.includes(authUserId) : false}
                {...feedback}
              />
            ))}
          </FeedbackList>
        </FeedbackCategory>

        <FeedbackCategory color="success" name="Completed" numberOfVotes={completedFeedbacks.length}>
          <FeedbackList className="mt-6">
            {completedFeedbacks.map((feedback) => (
              <FeedbackListItem key={feedback.id} completed {...feedback} />
            ))}
          </FeedbackList>
        </FeedbackCategory>
      </div>
      <FeedbackModal isOpen={isOpen} onClose={handleCloseFeedbackModal} />
    </>
  );
}
